import { c as _c } from "react/compiler-runtime";
import { useGetFaqCategoryQuery, useGetFaqNextCategoriesQuery } from '@/api/sanity/generated';
import { ContentContainer, Layout } from '@/components/layout';
import { LINKS } from '@/constants/links';
import HeroSimple from '@/sections/HeroSimple';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { DetailQuestion } from '@/sections/faq/DetailQuestion';
import { usePageView } from '@/analytics/pageTypes';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { type Category } from '@/sections/faq/NextCategory';
import { NextCategories } from '@/sections/faq/NextCategories';
import { SeoHeader } from '@/components/SeoHeader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { sanityLanguage } from '@/constants/language';
import { getActiveQuestionDetail, Question } from '@/sections/faq/functions/getActiveQuestionDetail';
import { getAllQuestionSlugs } from '@/sections/faq/functions/getAllQuestionSlugs';
interface PageProps extends IntlProps {
  category: Category;
  nextCategories: Category[];
  activeQuestion: Question;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(35);
  const {
    activeQuestion,
    category,
    nextCategories,
    menuPromoBanners
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["faq"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const t1 = activeQuestion?.name || "";
  let t2;
  if ($[1] !== activeQuestion?.slug?.current || $[2] !== category.slug.current) {
    t2 = LINKS().faqQuestion(category.slug.current || "", activeQuestion?.slug?.current || "");
    $[1] = activeQuestion?.slug?.current;
    $[2] = category.slug.current;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      title: t1,
      url: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  usePageView("faq-detail", t3);
  let t4;
  if ($[7] !== category.name || $[8] !== t) {
    t4 = t("faq:seo.categoryTitle", {
      category: category.name
    });
    $[7] = category.name;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("faq:seo.description");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <SeoHeader title={t4} description={t5} />;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      analyticsParam: "faqCategoryBack",
      testId: "faqCategoryBack",
      to: LINKS().faq
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== category.name) {
    t8 = <HeroSimple titleClassName="text-xl lg:text-5xl" circleClassName="left-auto right-0 lg:-translate-y-1/3 lg:translate-x-1/3 lg:h-[35rem] lg:w-[35rem]" title={category.name} withoutCharacters={true} backLink={t7} className="overflow-hidden bg-blue-50" />;
    $[16] = category.name;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== activeQuestion || $[19] !== category) {
    t9 = category.questions?.map((questionItem, index) => {
      if (!questionItem?.name) {
        return null;
      }
      const isActive = questionItem.slug?.current === activeQuestion?.slug?.current;
      return <DetailQuestion variant={isActive ? "open" : "closed"} questionDetail={isActive ? {
        ...activeQuestion,
        linkedFrom: category
      } : undefined} key={index} {...questionItem} slug={{
        current: LINKS().faqQuestion(category.slug.current || "", questionItem?.slug?.current || "")
      }} />;
    });
    $[18] = activeQuestion;
    $[19] = category;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <ContentContainer className="relative z-10 flex flex-col gap-2 px-4 md:max-w-192 md:gap-4">{t9}</ContentContainer>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="bgCircle absolute bottom-0 left-0 hidden -translate-x-1/4 rotate-90 lg:block" />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10) {
    t12 = <div className="relative bg-blue-50 pb-20 md:pb-40">{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== nextCategories) {
    t14 = <NextCategories nextCategories={nextCategories} />;
    $[27] = nextCategories;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== menuPromoBanners || $[30] !== t12 || $[31] !== t14 || $[32] !== t6 || $[33] !== t8) {
    t15 = <Layout menuPromoBanners={menuPromoBanners}>{t6}{t8}{t12}{t13}{t14}</Layout>;
    $[29] = menuPromoBanners;
    $[30] = t12;
    $[31] = t14;
    $[32] = t6;
    $[33] = t8;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
}
export const getStaticPaths = async () => {
  const {
    questionsSlugs
  } = await getAllQuestionSlugs();
  return {
    paths: questionsSlugs.map(item => ({
      params: {
        category: item.category,
        question: item.question
      }
    })),
    fallback: false
  };
};
const nextCategoriesIds = () => {
  if (process.env.NEXT_PUBLIC_LANGUAGE === 'hu') {
    return ['d97454e4-3ad8-449f-a190-5f2d7b592a5b', '2dd27096-53ce-4787-8e05-b4f818d9032a', 'd3b1f108-a23b-4a72-96bf-beac131a8b2a', '3a390f22-231f-48dd-a880-4c7adbae869a'];
  }
  if (process.env.NEXT_PUBLIC_LANGUAGE === 'sk') {
    return ['6BfMs6m5qGEYn3qsOWCBbG', '6Iyu1doyxlpMLwhkPsujmK', '2DwUXOK4Tws9NuPXiKBTbl', '12vL2KlrTy7tqbc1qWLwI4'];
  }
  if (process.env.NEXT_PUBLIC_TARGET_ENV === 'accept' || process.env.NEXT_PUBLIC_TARGET_ENV === 'nightly') {
    return ['6nV2Tpb7PwTJiS4RSLte7Z', '1KoTGhGVcmlZQQIr6IZbwo', '2tVFp1Ng5ctS7b0HvvN1uY'];
  }
  return ['7iCsxLitHoUqqRyE9ULjwV', '3mOvmE8yTjrrDsZJJyX4tJ', '4nQV5zahsGsZ7TiGXSSWbZ', '6nV2Tpb7PwTJiS4RSLte7Z'];
};
export const getStaticProps = (async ctx => {
  const categorySlug = ctx.params?.category as string;
  const questionSlug = ctx.params?.question as string;
  const fetchCategory = useGetFaqCategoryQuery.fetcher({
    slug: categorySlug,
    i18nLang: sanityLanguage
  });
  const fetchNextCategories = useGetFaqNextCategoriesQuery.fetcher({
    ids: nextCategoriesIds(),
    i18nLang: sanityLanguage
  });
  const category = await fetchCategory();
  const nextCategoriesData = await fetchNextCategories();
  const activeQuestion = await getActiveQuestionDetail(questionSlug);
  if (!activeQuestion) {
    return {
      notFound: true
    };
  }
  if (!category.allFaqCategory?.[0]) {
    return {
      notFound: true
    };
  }
  const nextCategories = nextCategoriesData.allFaqCategory.filter(category => category && category.name && category.slug !== categorySlug).slice(0, 3) as Category[];
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      category: category.allFaqCategory[0] as Category,
      nextCategories,
      activeQuestion,
      ...(await getIntlProps(ctx, ['faq']))
    }
  };
}) satisfies GetStaticProps<PageProps>;